/**
 * Plans (design "10 · Website & pricing": Pricing V0BsZ, Plan limits in the app k9qobd): each plan is a named set of
 * feature defaults and limits over the entitlement catalog (features.ts, layer 1 of docs/PERMISSIONS.md). Staff assign
 * a plan and its add-ons to a team from /dashboard/admin/orgs (accounts 0023: auth_organizations.plan / plan_addons);
 * staff overrides (org_entitlements) still apply on top.
 *
 *   effective = the plan's values (or the group defaults when the org has no plan)
 *             + its add-ons
 *             + staff overrides (org_entitlements, not expired)
 *
 * Today's behavior is kept: a plan's values ARE its group's defaults. Where the design asks for something else (Brief me
 * for offices at 20 a month, seat limits, 10-point alerts for offices, the API as a paid add-on), the difference is
 * listed in `designed` and NOT applied; staff can apply any of it to one org with an override, and the owner decides
 * whether it becomes the default (docs/PERMISSIONS.md §15).
 *
 * The privacy rule (layer 4) still wins: a privateData feature is never on for a team outside PRIVATE_DATA_GROUPS,
 * whatever its plan (planEntitlements uses mayGrant).
 *
 * Prices are the design's (USD; the market research frame marks them "proposed"). The public pricing page
 * (worker/src/landing.js) reads them from here.
 */
import { FEATURES, FEATURE_KEYS, mayGrant, type EntitlementGroup, type FeatureKey } from "./features";

export type PlanKey = "resident" | "office" | "advocacy";
export type AddonKey = "campaigns" | "disclosures" | "api" | "briefs";
export type FeatureValue = boolean | number | null;

export interface PlanDef {
  key: PlanKey;
  /** The design's name ("Ward Office", "Advocacy"). */
  name: string;
  /** The dashboard group whose defaults the plan starts from. */
  group: EntitlementGroup;
  /** Team plans can be assigned to an organization; the resident plan describes individual accounts. */
  team: boolean;
  /** USD a month: billed yearly (per month) and month to month. null = free. */
  price: { yearly: number; monthly: number } | null;
  seats: { included: number; extraMonthly: number } | null;
  nonprofitDiscount?: number;
  /** Every catalog feature's value on this plan (flags: on/off; limits: a number or null = none). */
  features: Record<FeatureKey, FeatureValue>;
  /** What the design shows that the code does not apply (yet): owner decision. */
  designed: Partial<Record<FeatureKey, FeatureValue>>;
  addons: AddonKey[];
}

export interface AddonDef {
  key: AddonKey;
  name: string;
  /** USD; `per` month or once. */
  price: number;
  per: "month" | "once";
  plans: PlanKey[];
  /**
   * Flags it turns on, and limits it raises (a number is added to the plan's limit when the plan has one; with no limit
   * it changes nothing). A feature key not in the catalog yet (disclosures) is skipped until it exists.
   */
  flags: string[];
  adds: Partial<Record<string, number>>;
}

/** The group defaults of every catalog feature (what an org gets today). */
export function groupDefaults(group: EntitlementGroup): Record<FeatureKey, FeatureValue> {
  return Object.fromEntries(FEATURE_KEYS.map((k) => [k, FEATURES[k].defaults[group] ?? null])) as Record<FeatureKey, FeatureValue>;
}

export const PLANS: Record<PlanKey, PlanDef> = {
  resident: {
    key: "resident", name: "Resident · Free", group: "resident", team: false, price: null, seats: null,
    features: groupDefaults("resident"), designed: {}, addons: [],
  },
  office: {
    key: "office", name: "Ward Office", group: "office", team: true,
    price: { yearly: 249, monthly: 299 }, seats: { included: 10, extraMonthly: 25 },
    features: groupDefaults("office"),
    designed: { briefMe: true, briefsPerMonth: 20, forecastAlerts: true, seats: 10 },
    addons: ["api", "briefs"],
  },
  advocacy: {
    key: "advocacy", name: "Advocacy", group: "advocacy", team: true,
    price: { yearly: 399, monthly: 479 }, seats: { included: 5, extraMonthly: 49 }, nonprofitDiscount: 0.25,
    features: groupDefaults("advocacy"),
    designed: { seats: 5 },
    addons: ["campaigns", "disclosures", "api"],
  },
};
export const PLAN_KEYS = Object.keys(PLANS) as PlanKey[];
export const TEAM_PLAN_KEYS = PLAN_KEYS.filter((k) => PLANS[k].team);

export const ADDONS: Record<AddonKey, AddonDef> = {
  campaigns:   { key: "campaigns", name: "Campaigns and supporter action pages", price: 199, per: "month", plans: ["advocacy"], flags: ["campaigns"], adds: {} },
  disclosures: { key: "disclosures", name: "Quarterly lobbying disclosure drafts", price: 99, per: "month", plans: ["advocacy"], flags: ["disclosures"], adds: {} },
  api:         { key: "api", name: "API and embeds", price: 150, per: "month", plans: ["office", "advocacy"], flags: ["apiAccess"], adds: {} },
  briefs:      { key: "briefs", name: "20 more briefs", price: 49, per: "once", plans: ["office"], flags: [], adds: { briefsPerMonth: 20 } },
};
export const ADDON_KEYS = Object.keys(ADDONS) as AddonKey[];

export const isPlanKey = (k: unknown): k is PlanKey => typeof k === "string" && (PLAN_KEYS as string[]).includes(k);
export const isAddonKey = (k: unknown): k is AddonKey => typeof k === "string" && (ADDON_KEYS as string[]).includes(k);

/** "campaigns api" → ["campaigns", "api"] (unknown names dropped, de-duplicated, catalog order). */
export function parseAddons(v: unknown): AddonKey[] {
  const list = Array.isArray(v) ? v : typeof v === "string" ? v.split(/[\s,]+/) : [];
  return ADDON_KEYS.filter((k) => list.includes(k));
}

/** The plan that has a feature on, other than `except` (the lock badge: "Advocacy"). Team plans only. */
export function planWith(feature: string, except?: PlanKey | null): PlanDef | null {
  return TEAM_PLAN_KEYS.map((k) => PLANS[k]).find((p) => p.key !== except && (p.features as Record<string, FeatureValue>)[feature] === true) ?? null;
}

/** The plan a group is on when no plan is assigned. */
export const planForGroup = (group: string | null | undefined): PlanKey | null =>
  group === "office" || group === "platform" ? "office" : group === "advocacy" ? "advocacy" : group === "resident" ? "resident" : null;

/**
 * Flags and limits for an org of `group` on `plan` with `addons` (before staff overrides). No plan: the group defaults.
 * Add-ons not offered on the plan are ignored; a privateData flag stays off outside PRIVATE_DATA_GROUPS.
 */
export function planValues(group: EntitlementGroup, plan: PlanKey | null | undefined, addons: AddonKey[] = []): Record<FeatureKey, FeatureValue> {
  const p = plan && PLANS[plan] ? PLANS[plan] : null;
  const out = { ...(p ? p.features : groupDefaults(group)) } as Record<string, FeatureValue>;
  for (const k of FEATURE_KEYS) if (FEATURES[k].kind === "flag" && out[k] === true && !mayGrant(k, group)) out[k] = false;
  if (p) {
    for (const a of addons) {
      const d = ADDONS[a];
      if (!d || !d.plans.includes(p.key)) continue;
      for (const f of d.flags) if (f in FEATURES && mayGrant(f, group)) out[f] = true;
      for (const [f, n] of Object.entries(d.adds)) if (f in FEATURES && typeof out[f] === "number" && typeof n === "number") out[f] = (out[f] as number) + n;
    }
  }
  return out as Record<FeatureKey, FeatureValue>;
}

/** USD formatting for plan prices ("$2,988"). */
export const usd = (n: number) => `$${n.toLocaleString("en-US")}`;
